/**
 * MermaidBlock — renders a mermaid diagram from source text.
 * Used in both read-only MarkdownRenderer and TipTap editor NodeView.
 */
import { useState, useEffect, useRef, useId } from "react";

// Mermaid is ~800KB+ (pulls in d3/dagre/cytoscape). Load it lazily on first
// diagram render so it stays out of the main bundle — most pages (e.g. the
// Enable landing) never render a diagram and shouldn't pay for it.
type Mermaid = typeof import("mermaid").default;
let mermaidPromise: Promise<Mermaid> | null = null;
function loadMermaid(): Promise<Mermaid> {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then((m) => {
      const mermaid = m.default;
      mermaid.initialize({
        startOnLoad: false,
        theme: "neutral",
        // mermaid 12 defaults to the ELK layout and the "neo" look; pin the
        // mermaid 11 rendering so existing diagrams keep their layout
        layout: "dagre",
        look: "classic",
        securityLevel: "loose",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
      });
      return mermaid;
    });
  }
  return mermaidPromise;
}

interface MermaidBlockProps {
  source: string;
  className?: string;
}

export default function MermaidBlock({ source, className = "" }: MermaidBlockProps) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId().replace(/:/g, "_");

  useEffect(() => {
    if (!source.trim()) {
      setSvg(null);
      setError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const mermaid = await loadMermaid();
        const { svg: rendered } = await mermaid.render(`mermaid-${uniqueId}`, source.trim());
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setSvg(null);
          setError(err?.message || "Invalid mermaid syntax");
        }
        // Clean up any leftover error element mermaid may have injected
        const errEl = document.getElementById(`d${uniqueId}`);
        errEl?.remove();
      }
    })();

    return () => { cancelled = true; };
  }, [source, uniqueId]);

  // `data-print-atomic` marks the diagram as a non-cuttable block for
  // the print paginator (see `NO_CUT_INSIDE_SELECTOR` in
  // usePageBoundaries). Without it, the paginator's fixed-mode hard
  // cut can slice mermaid SVGs in half across pages.
  if (error) {
    return (
      <div
        data-print-atomic=""
        className={`rounded-lg border border-red-200 bg-red-50 p-4 ${className}`}
      >
        <div className="flex items-center gap-2 text-xs text-red-600 font-medium mb-2">
          <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          Mermaid syntax error
        </div>
        <pre className="text-[11px] text-red-500 whitespace-pre-wrap font-mono">{error}</pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div
        data-print-atomic=""
        className={`rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-400 ${className}`}
      >
        Rendering diagram…
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-print-atomic=""
      className={`cortex-mermaid rounded-lg border border-slate-200 bg-white p-4 overflow-x-auto ${className}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
